/**
 * ConsentAuditPanel · v2.7.65+E168
 * ─────────────────────────────────────────────────────────────────────────
 * Admin → User Management → "Consent Audit" tab.
 *
 * Read-only view over every rider + driver row keyed by TCPA SMS opt-in
 * (`sms_consent` + `sms_consent_ts`). Lets outbound-campaign owners see
 * exactly who is safe to text and when they opted in.
 *
 * Filters:
 *   · Role     — Rider / Driver / All
 *   · Consent  — Opted-in / Opted-out / Unknown / All
 *   · Search   — case-insensitive across name / email / phone
 *
 * Backed by GET /api/admin/consent-audit (JSON) and
 * GET /api/admin/consent-audit/export.csv (attachment download).
 *
 * This panel does NOT send any SMS — it purely reads. All outbound
 * behavior stays gated by the notify_kill_switch + Twilio dry-run.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  ShieldCheck, ShieldOff, ShieldQuestion, Users, User, Car,
  Search, Download, Loader2, RefreshCw, Radio, MessageSquareOff,
  MessageSquareText, ArchiveX,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const ROLE_CHIPS = [
  { value: "all",    label: "All",     Icon: Users, color: "amber" },
  { value: "rider",  label: "Riders",  Icon: User,  color: "violet" },
  { value: "driver", label: "Drivers", Icon: Car,   color: "sky" },
];

const CONSENT_CHIPS = [
  { value: "all",     label: "All",        Icon: Users,           tint: "border-zinc-700 text-zinc-200" },
  { value: "yes",     label: "Opted-in",   Icon: ShieldCheck,     tint: "border-emerald-500/50 text-emerald-300 bg-emerald-500/5" },
  { value: "no",      label: "Opted-out",  Icon: ShieldOff,       tint: "border-rose-500/50 text-rose-300 bg-rose-500/5" },
  { value: "unknown", label: "Unknown",    Icon: ShieldQuestion,  tint: "border-zinc-500/50 text-zinc-300 bg-zinc-800/40" },
];

// v2.7.65+E170 · Provenance chips — surface who opted in via self-signup
// vs who opted out mid-flight via a Twilio STOP reply.
const SOURCE_CHIPS = [
  { value: "all",                label: "All",         Icon: Users,             tint: "border-zinc-700 text-zinc-200" },
  { value: "self_signup",        label: "Self-signup", Icon: Radio,             tint: "border-violet-500/50 text-violet-300 bg-violet-500/5" },
  { value: "twilio_stop_reply",  label: "STOP reply",  Icon: MessageSquareOff,  tint: "border-rose-500/50 text-rose-300 bg-rose-500/5" },
  { value: "twilio_start_reply", label: "START reply", Icon: MessageSquareText, tint: "border-emerald-500/50 text-emerald-300 bg-emerald-500/5" },
  { value: "none",               label: "Legacy",      Icon: ArchiveX,          tint: "border-amber-500/50 text-amber-300 bg-amber-500/5" },
];

const SOURCE_LABEL = {
  self_signup: "Self-signup",
  twilio_stop_reply: "STOP reply",
  twilio_start_reply: "START reply",
};

const PAGE_SIZE = 100;

function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch { return iso; }
}

export default function ConsentAuditPanel() {
  // v2.7.65+E171 · Read `source` from URL hash so the StopRateLedgerCard on
  // the Overview can deep-link to a pre-filtered view.
  const initialSource = (() => {
    try {
      const h = window.location.hash || "";
      const q = h.includes("?") ? h.split("?")[1] : h.replace(/^#/, "");
      const params = new URLSearchParams(q);
      const s = params.get("source");
      const allowed = ["all", "self_signup", "twilio_stop_reply", "twilio_start_reply", "none"];
      return allowed.includes(s) ? s : "all";
    } catch { return "all"; }
  })();

  const [role, setRole] = useState("all");
  const [consent, setConsent] = useState("all");
  const [source, setSource] = useState(initialSource);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [data, setData] = useState({ total: 0, opted_in: 0, opted_out: 0, unknown: 0, source_counts: {}, results: [] });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => { setPage(0); }, [role, consent, source, qDebounced]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        role, consent, source,
        limit: String(PAGE_SIZE),
        skip: String(page * PAGE_SIZE),
      });
      if (qDebounced) params.set("q", qDebounced);
      const { data: resp } = await api.get(`/admin/consent-audit?${params.toString()}`);
      setData(resp || { total: 0, opted_in: 0, opted_out: 0, unknown: 0, source_counts: {}, results: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load consent audit");
    } finally {
      setLoading(false);
    }
  }, [role, consent, source, qDebounced, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ role, consent, source });
      if (qDebounced) params.set("q", qDebounced);
      const resp = await api.get(
        `/admin/consent-audit/export.csv?${params.toString()}`,
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `buller-taxi-consent-audit-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${data.total} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="space-y-4" data-testid="consent-audit-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-extrabold tracking-tight text-zinc-100 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-400"/>SMS Consent Audit
          </h3>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl leading-relaxed">
            Every rider + driver row with their TCPA opt-in status and timestamp.
            Filter to <span className="text-emerald-300 font-semibold">Opted-in</span> before
            starting any outbound SMS campaign so unconsented numbers are never contacted.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            data-testid="consent-audit-refresh"
            className="border-zinc-700 bg-zinc-900 hover:bg-zinc-800 h-9"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`}/>Refresh
          </Button>
          <Button
            onClick={downloadCsv}
            disabled={exporting || loading || data.total === 0}
            data-testid="consent-audit-export"
            className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-widest text-xs h-9"
          >
            {exporting
              ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin"/>Exporting…</>
              : <><Download className="h-3.5 w-3.5 mr-1.5"/>Export CSV</>}
          </Button>
        </div>
      </div>

      {/* Summary counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="consent-audit-summary">
        <Counter label="Total"       value={data.total}        icon={<Users className="h-4 w-4"/>}         tint="text-zinc-100 border-zinc-700 bg-zinc-900" testid="consent-audit-count-total"/>
        <Counter label="Opted-in"    value={data.opted_in}     icon={<ShieldCheck className="h-4 w-4"/>}   tint="text-emerald-200 border-emerald-500/40 bg-emerald-500/5" testid="consent-audit-count-opted-in"/>
        <Counter label="Opted-out"   value={data.opted_out}    icon={<ShieldOff className="h-4 w-4"/>}     tint="text-rose-200 border-rose-500/40 bg-rose-500/5" testid="consent-audit-count-opted-out"/>
        <Counter label="Unknown"     value={data.unknown}      icon={<ShieldQuestion className="h-4 w-4"/>} tint="text-zinc-200 border-zinc-600 bg-zinc-800/40" testid="consent-audit-count-unknown"/>
      </div>

      {/* Filter chips */}
      <Card className="bg-zinc-900/60 border-zinc-800 p-3" data-testid="consent-audit-filters">
        <div className="flex flex-wrap gap-3 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Role</div>
            <div className="flex flex-wrap gap-1.5">
              {ROLE_CHIPS.map(({ value, label, Icon }) => {
                const active = role === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setRole(value)}
                    data-testid={`consent-audit-role-${value}`}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold transition ${
                      active
                        ? "bg-amber-400 border-amber-400 text-zinc-950"
                        : "bg-zinc-950 border-zinc-700 text-zinc-300 hover:border-amber-400/50"
                    }`}
                  >
                    <Icon className="h-3 w-3"/>{label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="h-8 w-px bg-zinc-800 hidden md:block"/>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Consent</div>
            <div className="flex flex-wrap gap-1.5">
              {CONSENT_CHIPS.map(({ value, label, Icon, tint }) => {
                const active = consent === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setConsent(value)}
                    data-testid={`consent-audit-consent-${value}`}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold transition ${
                      active
                        ? "bg-amber-400 border-amber-400 text-zinc-950"
                        : `bg-zinc-950 ${tint} hover:border-amber-400/50`
                    }`}
                  >
                    <Icon className="h-3 w-3"/>{label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="h-8 w-px bg-zinc-800 hidden md:block"/>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Source</div>
            <div className="flex flex-wrap gap-1.5">
              {SOURCE_CHIPS.map(({ value, label, Icon, tint }) => {
                const active = source === value;
                const count = value === "all"
                  ? Object.values(data.source_counts || {}).reduce((a, b) => a + b, 0)
                  : (data.source_counts || {})[value] || 0;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setSource(value)}
                    data-testid={`consent-audit-source-${value}`}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold transition ${
                      active
                        ? "bg-amber-400 border-amber-400 text-zinc-950"
                        : `bg-zinc-950 ${tint} hover:border-amber-400/50`
                    }`}
                  >
                    <Icon className="h-3 w-3"/>{label}
                    {count > 0 && (
                      <span className={`text-[10px] font-mono ${active ? "text-zinc-900" : "opacity-70"}`}>
                        · {count.toLocaleString()}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="h-8 w-px bg-zinc-800 hidden md:block"/>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Search</div>
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2"/>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Name, email, or phone"
                data-testid="consent-audit-search"
                className="pl-8 bg-zinc-950 border-zinc-700 h-8 text-sm"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Results table */}
      <Card className="bg-zinc-900/60 border-zinc-800 overflow-hidden" data-testid="consent-audit-table-wrap">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="consent-audit-table">
            <thead className="bg-zinc-950/70 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
              <tr>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Phone</th>
                <th className="text-left px-3 py-2">Consent</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Opted-in At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading && data.results.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-zinc-500">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto"/>
                </td></tr>
              ) : data.results.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-zinc-500" data-testid="consent-audit-empty">
                  No accounts match these filters.
                </td></tr>
              ) : data.results.map((row, i) => (
                <tr key={`${row.role}-${row.id || i}`} data-testid={`consent-audit-row-${row.role}-${row.id || i}`} className="hover:bg-amber-400/5">
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={row.role === "driver"
                        ? "border-sky-500/40 text-sky-300 bg-sky-500/5"
                        : "border-violet-500/40 text-violet-300 bg-violet-500/5"}>
                      {row.role === "driver" ? "Driver" : "Rider"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-zinc-100 font-semibold">{row.name || "—"}</td>
                  <td className="px-3 py-2 text-zinc-400 text-xs">{row.email || "—"}</td>
                  <td className="px-3 py-2 text-zinc-300 text-xs font-mono">{row.phone || "—"}</td>
                  <td className="px-3 py-2">
                    <ConsentBadge v={row.sms_consent}/>
                  </td>
                  <td className="px-3 py-2">
                    <SourceBadge s={row.sms_consent_source}/>
                  </td>
                  <td className="px-3 py-2 text-zinc-400 text-xs">{formatTs(row.sms_consent_ts)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data.total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 px-3 py-2 border-t border-zinc-800 text-xs text-zinc-400" data-testid="consent-audit-pagination">
            <div>
              Page <span className="text-zinc-100 font-bold">{page + 1}</span> of <span className="text-zinc-100 font-bold">{totalPages}</span>
              {" · "}
              {data.results.length} of {data.total} shown
            </div>
            <div className="flex gap-1.5">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0 || loading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                data-testid="consent-audit-prev"
                className="border-zinc-700 bg-zinc-950 h-7 text-xs"
              >Prev</Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page + 1 >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                data-testid="consent-audit-next"
                className="border-zinc-700 bg-zinc-950 h-7 text-xs"
              >Next</Button>
            </div>
          </div>
        )}
      </Card>

      <p className="text-[10px] text-zinc-500 leading-relaxed" data-testid="consent-audit-footer-note">
        <span className="text-amber-300 font-bold">Compliance reminder</span> · Only phone numbers whose row
        shows <span className="text-emerald-300 font-semibold">Opted-in</span> may receive marketing
        or non-transactional SMS. Transactional dispatch notifications continue to obey the
        global kill switch and each account&apos;s per-booking preferences.
      </p>
    </div>
  );
}

function Counter({ label, value, icon, tint, testid }) {
  return (
    <div className={`rounded-lg border px-3 py-2.5 flex items-center gap-3 ${tint}`} data-testid={testid}>
      <div className="opacity-80">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold">{label}</div>
        <div className="meter-display text-xl font-extrabold" data-testid={`${testid}-value`}>{value.toLocaleString()}</div>
      </div>
    </div>
  );
}

function ConsentBadge({ v }) {
  if (v === true) return (
    <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 font-bold" data-testid="consent-badge-yes">
      <ShieldCheck className="h-3 w-3 mr-1"/>Opted-in
    </Badge>
  );
  if (v === false) return (
    <Badge className="bg-rose-500/15 text-rose-300 border border-rose-500/40 font-bold" data-testid="consent-badge-no">
      <ShieldOff className="h-3 w-3 mr-1"/>Opted-out
    </Badge>
  );
  return (
    <Badge className="bg-zinc-800 text-zinc-300 border border-zinc-600 font-bold" data-testid="consent-badge-unknown">
      <ShieldQuestion className="h-3 w-3 mr-1"/>Unknown
    </Badge>
  );
}

// v2.7.65+E170 · Mid-flight opt-out visibility — makes it obvious at a
// glance whether the consent value came from a Twilio STOP reply vs the
// original self-signup checkbox.
function SourceBadge({ s }) {
  if (s === "twilio_stop_reply") return (
    <Badge className="bg-rose-500/15 text-rose-300 border border-rose-500/40 font-bold" data-testid="source-badge-stop">
      <MessageSquareOff className="h-3 w-3 mr-1"/>STOP reply
    </Badge>
  );
  if (s === "twilio_start_reply") return (
    <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 font-bold" data-testid="source-badge-start">
      <MessageSquareText className="h-3 w-3 mr-1"/>START reply
    </Badge>
  );
  if (s === "self_signup") return (
    <Badge className="bg-violet-500/15 text-violet-300 border border-violet-500/40 font-bold" data-testid="source-badge-self">
      <Radio className="h-3 w-3 mr-1"/>Self-signup
    </Badge>
  );
  if (s) return (
    <Badge className="bg-zinc-800 text-zinc-300 border border-zinc-600 font-bold" data-testid="source-badge-other">
      {SOURCE_LABEL[s] || s}
    </Badge>
  );
  return (
    <Badge className="bg-zinc-800 text-zinc-500 border border-zinc-700 font-bold" data-testid="source-badge-none">
      <ArchiveX className="h-3 w-3 mr-1"/>Legacy
    </Badge>
  );
}
